import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgePercent, PackageCheck, Store, Wallet } from "lucide-react";
import { shopApi, useShop } from "./ShopContext";
import { ProductCard } from "./ProductCard";

const HERO = "https://images.unsplash.com/photo-1631856954655-966f97d809de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Perk = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
    <span className="rounded-lg bg-[#FEF3EC] p-2 text-[#D96B27]"><Icon className="h-5 w-5" /></span>
    <div><p className="text-sm font-bold text-slate-900">{title}</p><p className="text-xs text-slate-500">{text}</p></div>
  </div>
);

const Row = ({ title, sort, testid }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { shopApi.get("/products", { params: { sort, limit: 10 } }).then(({ data }) => setItems(data.products)).catch(() => {}); }, [sort]);
  if (!items.length) return null;
  return (
    <section className="mt-10" data-testid={testid}>
      <div className="mb-4 flex items-end justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
        <Link to={`/shop/products?sort=${sort}`} className="flex items-center gap-1 text-sm font-semibold text-[#D96B27] hover:underline">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
    </section>
  );
};

export default function ShopHome() {
  const { customer } = useShop();
  const [departments, setDepartments] = useState([]);
  useEffect(() => { shopApi.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {}); }, []);
  return (
    <div data-testid="shop-home">
      <section className="relative overflow-hidden rounded-2xl bg-[#1E293B]">
        <img src={HERO} alt="Hardware store aisle" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative max-w-2xl px-6 py-14 sm:px-10 sm:py-20">
          <p className="mb-3 inline-block rounded-full bg-[#D96B27] px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Order online · Collect in store</p>
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Everything for the build, the farm and the home.
          </h1>
          <p className="mt-4 text-base text-slate-200">Shop real shelf stock from Bosveld Bou & Hardware. We pack it, you collect it and pay at the counter.</p>
          <Link to="/shop/products" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D96B27] px-6 py-3 font-bold text-white transition-colors hover:bg-[#C2591B]" data-testid="shop-hero-cta">
            Start shopping <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Perk icon={Store} title="Collect FREE in store" text="We'll WhatsApp you when it's packed" />
        <Perk icon={Wallet} title="Pay when you collect" text="Cash, card or your store account" />
        <Perk icon={PackageCheck} title="Live shelf stock" text="Only what's really on our shelves" />
      </div>

      {!customer && (
        <Link to="/shop/login" className="mt-6 flex items-center gap-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white" data-testid="shop-discount-banner">
          <BadgePercent className="h-6 w-6 shrink-0" />
          <span className="text-sm font-semibold">Have a store account? Sign in to see your own discount prices.</span>
          <ArrowRight className="ml-auto h-4 w-4" />
        </Link>
      )}

      {departments.length > 0 && (
        <section className="mt-10" data-testid="shop-departments">
          <h2 className="mb-4 text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Shop by department</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {departments.slice(0, 12).map((d) => (
              <Link key={d.name} to={`/shop/products?department=${encodeURIComponent(d.name)}`}
                className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-[#D96B27] hover:bg-[#FEF3EC]" data-testid={`shop-dept-${d.name}`}>
                <p className="text-sm font-bold text-slate-800">{d.name}</p>
                <p className="text-xs text-slate-500">{d.count} products</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Row title="New on the shelves" sort="newest" testid="shop-row-new" />
      <Row title="Popular picks" sort="name" testid="shop-row-popular" />
    </div>
  );
}
